import { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, ShieldCheck, Truck, Clock, Phone, Star, TrendingUp } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { productService } from '../services/api'
import ProductCard from '../components/product/ProductCard'
import { SectionHeader } from '../components/ui'
import { useAuth } from '../context/AuthContext'

// ── Full category taxonomy from client data ──────────────────────────────────
const CATEGORY_GROUPS = [
  {
    label: 'Alcoholic Beverages',
    emoji: '🍾',
    gradient: 'from-teal-900/90 to-teal-700/70',
    accent: 'border-teal-500/40',
    sub: [
      { label: 'Beer (Mainstream)', emoji: '🍺', query: 'beer' },
      { label: 'Beer — Craft', emoji: '🍻', query: 'craft beer' },
      { label: 'Non-Alcoholic Beer', emoji: '🥛', query: 'non alcoholic beer' },
      { label: 'Wine', emoji: '🍷', query: 'wine' },
      { label: 'Champagne & Sparkling', emoji: '🥂', query: 'champagne' },
      { label: 'Non-Alcoholic Wine', emoji: '🍇', query: 'non alcoholic wine' },
      { label: 'Port, Sherry & Muscadel', emoji: '🫙', query: 'port sherry' },
      { label: 'Box Wine', emoji: '📦', query: 'box wine' },
      { label: 'Gin', emoji: '🌿', query: 'gin' },
      { label: 'Vodka', emoji: '🍸', query: 'vodka' },
      { label: 'Whiskey', emoji: '🥃', query: 'whiskey' },
      { label: 'Rum', emoji: '🏝️', query: 'rum' },
      { label: 'Tequila', emoji: '🌵', query: 'tequila' },
      { label: 'Brandy', emoji: '🍂', query: 'brandy' },
      { label: 'Liqueur', emoji: '✨', query: 'liqueur' },
      { label: 'Aperitifs', emoji: '🫗', query: 'aperitif' },
      { label: 'Cane', emoji: '🌾', query: 'cane' },
      { label: 'Shooters', emoji: '🔫', query: 'shooters' },
      { label: 'Other Spirits', emoji: '🥄', query: 'spirits' },
      { label: 'Ciders & RTDs', emoji: '🍋', query: 'cider' },
      { label: 'Non-Alcoholic RTD', emoji: '🧴', query: 'non alcoholic rtd' },
      { label: 'RTD Cocktails', emoji: '🍹', query: 'rtd cocktail' },
    ],
  },
  {
    label: 'Non-Alcoholic Beverages',
    emoji: '🥤',
    gradient: 'from-blue-900/90 to-cyan-800/70',
    accent: 'border-blue-500/40',
    sub: [
      { label: 'Minerals', emoji: '💧', query: 'minerals' },
      { label: 'Mineral Water', emoji: '🫧', query: 'water' },
      { label: 'Mineral Drinks', emoji: '🧃', query: 'mineral drinks' },
      { label: 'Juice', emoji: '🍊', query: 'juice' },
      { label: 'Energy Drinks', emoji: '⚡', query: 'energy' },
      { label: 'Cordials', emoji: '🍓', query: 'cordials' },
    ],
  },
  {
    label: 'Tobacco',
    emoji: '🚬',
    gradient: 'from-gray-800/90 to-gray-700/70',
    accent: 'border-gray-500/40',
    sub: [
      { label: 'Cigarettes', emoji: '🚬', query: 'cigarettes' },
    ],
  },
  {
    label: 'Accessories & More',
    emoji: '🎁',
    gradient: 'from-gold/30 to-amber-800/50',
    accent: 'border-gold/40',
    sub: [
      { label: 'Glassware', emoji: '🥂', query: 'glassware' },
      { label: 'Gifts', emoji: '🎁', query: 'gifts' },
      { label: 'Snacks', emoji: '🍿', query: 'snacks' },
    ],
  },
]

const FEATURES = [
  { icon: Truck, title: 'Bulk Orders', desc: 'Large volume orders for businesses & events', color: 'text-primary' },
  { icon: ShieldCheck, title: 'Trusted Supplier', desc: 'Licensed distributor since 1992', color: 'text-gold' },
  { icon: Clock, title: 'Fast Processing', desc: 'Orders processed same day before 2PM', color: 'text-primary' },
  { icon: Phone, title: 'Expert Support', desc: 'Call 012 323 1520 for pricing queries', color: 'text-gold' },
]

export default function Home() {
  const { canViewAlcohol } = useAuth()
  const { data: featuredData, isLoading } = useQuery({
    queryKey: ['products', 'featured'],
    queryFn: () => productService.getAll({ limit: 8, sort: 'newest' }),
    staleTime: 1000 * 60 * 5,
  })

  const products = featuredData?.items || featuredData || []

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <HeroSection canViewAlcohol={canViewAlcohol} />

      {/* Features strip */}
      <section className="bg-gray-50 dark:bg-gray-800/50 border-y border-gray-100 dark:border-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map(({ icon: Icon, title, desc, color }) => (
            <div key={title} className="flex items-start gap-3 py-2">
              <Icon className={`${color} mt-0.5 flex-shrink-0`} size={22} />
              <div>
                <div className="font-display font-semibold text-gray-900 dark:text-white text-sm">{title}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-14 px-4 max-w-7xl mx-auto">
        <SectionHeader
          title="Shop by Category"
          subtitle="Browse our full range — from premium spirits to everyday beverages"
          action={
            <Link to="/shop" className="text-primary dark:text-red-400 text-sm font-display font-medium hover:underline flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          }
        />
        <CategoryBrowser />
      </section>

      {/* Featured / New Arrivals */}
      <section className="py-6 pb-14 px-4 max-w-7xl mx-auto">
        <SectionHeader
          title="New Arrivals"
          subtitle="Fresh stock just landed"
          action={
            <Link to="/shop" className="btn-outline text-sm py-2">
              See All Products
            </Link>
          }
        />
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="card animate-pulse">
                <div className="aspect-square skeleton" />
                <div className="p-4 space-y-2">
                  <div className="h-3 skeleton rounded w-1/2" />
                  <div className="h-4 skeleton rounded w-3/4" />
                  <div className="h-5 skeleton rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        ) : (
          <DemoProducts />
        )}
      </section>

      {/* Promo banner */}
      <PromoBanner />

      {/* Stats */}
      <StatsSection />
    </div>
  )
}

// ── CategoryBrowser ───────────────────────────────────────────────────────────
// Four top-level cards; clicking one expands the sub-category chips below it.
function CategoryBrowser() {
  const [open, setOpen] = useState(null)
  const navigate = useNavigate()

  return (
    <div className="space-y-3">
      {/* Top-level group cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {CATEGORY_GROUPS.map((group, i) => {
          const isOpen = open === i
          return (
            <button
              key={group.label}
              onClick={() => setOpen(isOpen ? null : i)}
              className={`relative rounded-2xl overflow-hidden border-2 transition-all duration-300 text-left
                ${isOpen ? `${group.accent} scale-[1.02] shadow-lg shadow-black/30` : 'border-transparent hover:border-white/10'}
              `}
            >
              {/* Gradient bg */}
              <div className={`absolute inset-0 bg-gradient-to-br ${group.gradient}`} />
              <div className="absolute inset-0 bg-black/20" />

              <div className="relative p-5 flex items-center gap-3">
                <span className="text-3xl leading-none flex-shrink-0">{group.emoji}</span>
                <div>
                  <div className="font-display font-bold text-white text-sm leading-tight">{group.label}</div>
                  <div className="text-white/60 text-xs mt-0.5">{group.sub.length} categories</div>
                </div>
                <div className={`ml-auto transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
                  <ArrowRight size={14} className="text-white/50 rotate-90" />
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Expanded sub-category chip panel */}
      {open !== null && (
        <div className={`rounded-2xl border bg-gray-50 dark:bg-gray-900/80 backdrop-blur-sm p-5 animate-fade-in ${CATEGORY_GROUPS[open].accent}`}>
          <p className="text-xs font-display font-semibold text-gray-400 uppercase tracking-widest mb-3">
            {CATEGORY_GROUPS[open].label}
          </p>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_GROUPS[open].sub.map(sub => (
              <button
                key={sub.query}
                onClick={() => navigate(`/shop?category=${encodeURIComponent(sub.query)}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-sm font-display text-gray-700 dark:text-gray-200 hover:text-primary group"
              >
                <span>{sub.emoji}</span>
                <span>{sub.label}</span>
              </button>
            ))}
            <button
              onClick={() => navigate(`/shop`)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 hover:bg-primary hover:text-white transition-all text-sm font-display text-primary"
            >
              <ArrowRight size={12} /> View all
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── HeroSection ───────────────────────────────────────────────────────────────
// 1. The outer wrapper has enough height for the viewport-height section PLUS
//    the hidden portion of the image (extraHeight). This creates real scroll distance.
// 2. While the top of the wrapper is still above the viewport top, the inner
//    section is position:sticky so it stays fixed in view.
// 3. We track how far into that sticky range the user has scrolled and use it
//    to translate the image upward — revealing the bottom that was cropped.
// 4. Once the user has scrolled past the extra height, sticky releases naturally
//    and the page continues scrolling normally.

const VIEWPORT_H = 560   // visible banner height in px
const IMAGE_EXTRA = 220  // how much extra image height is hidden below (tune to your images)
const TOTAL_H = VIEWPORT_H + IMAGE_EXTRA

function HeroSection({ canViewAlcohol = true }) {
  const [slide, setSlide] = useState(0)
  const [imgOffset, setImgOffset] = useState(0)
  const wrapperRef = useRef(null)
  const rafRef = useRef(null)

  // Pick a random variant (0–3) once per mount for each slide
  const [slideVariants] = useState(() => [
    Math.floor(Math.random() * 4),
    Math.floor(Math.random() * 4),
    Math.floor(Math.random() * 4),
    Math.floor(Math.random() * 4),
  ])

  const alcoholSlides = [
    {
      heading: "Fine Wines &\nPremium Spirits",
      sub: "Curated selection of premium wines and top-shelf spirits.",
      cta: "Explore Spirits",
      ctaLink: "/shop?category=spirits",
      badge: "🍷 Premium selection",
      image: `/images/banners/banner-whisky-${slideVariants[1]}.webp`,
    },
    {
      heading: "Craft Beer\nSelection",
      sub: "From local craft to international lagers — cold and ready.",
      cta: "Browse Beer",
      ctaLink: "/shop?category=beer",
      badge: "🍺 Ice cold selection",
      image: `/images/banners/banner-beer-${slideVariants[2]}.webp`,
    },
    {
      heading: "Wines for Every\nOccasion",
      sub: "Red, white, rosé and sparkling — wholesale pricing available.",
      cta: "Browse Wine",
      ctaLink: "/shop?category=wine",
      badge: "🍾 Cellar to door",
      image: `/images/banners/banner-wine-${slideVariants[3]}.webp`,
    },
  ]

  const nonAlcoholSlides = [
    {
      heading: "Energy Drinks\n& Cold Beverages",
      sub: "Top energy drink brands — bulk pricing for retailers & events.",
      cta: "Shop Energy Drinks",
      ctaLink: "/shop?category=energy",
      badge: "⚡ Bulk pricing",
      image: `/images/banners/banner-hero-${slideVariants[1]}.webp`,
    },
    {
      heading: "Water & Soft\nDrinks",
      sub: "Still, sparkling and flavoured waters — pallets welcome.",
      cta: "Browse Beverages",
      ctaLink: "/shop?category=water",
      badge: "💧 Stay refreshed",
      image: `/images/banners/banner-hero-${slideVariants[2]}.webp`,
    },
    {
      heading: "Snacks &\nAccessories",
      sub: "Perfect pairings — snacks, glassware and event essentials.",
      cta: "Browse Snacks",
      ctaLink: "/shop?category=snacks",
      badge: "🍿 Event ready",
      image: `/images/banners/banner-hero-${slideVariants[3]}.webp`,
    },
  ]

  const slides = [
    {
      heading: "Your Premier\nLiquor Distributor",
      sub: "Thousands of products for restaurants, bars, events & retailers.",
      cta: "Shop Now",
      ctaLink: "/shop",
      badge: "🏆 Trusted since 1992",
      image: `/images/banners/banner-hero-${slideVariants[0]}.webp`,
    },
    ...(canViewAlcohol ? alcoholSlides : nonAlcoholSlides),
  ]

  // Reset slide index when canViewAlcohol changes (avoids out-of-bounds)
  useEffect(() => { setSlide(0) }, [canViewAlcohol])

  // Auto-advance slides
  useEffect(() => {
    const t = setInterval(() => setSlide(s => (s + 1) % slides.length), 5500)
    return () => clearInterval(t)
  }, [slides.length])

  // Scroll handler — compute how far into the sticky zone we are
  const onScroll = useCallback(() => {
    if (!wrapperRef.current) return
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      const rect = wrapperRef.current.getBoundingClientRect()
      // rect.top goes negative as we scroll down into the sticky zone
      // When rect.top === 0: we just hit the sticky point — offset = 0
      // When rect.top === -IMAGE_EXTRA: we've scrolled through all extra — offset = IMAGE_EXTRA
      const scrolledIn = Math.max(0, Math.min(-rect.top, IMAGE_EXTRA))
      setImgOffset(scrolledIn)
    })
  }, [])

  useEffect(() => {
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [onScroll])

  const current = slides[slide]

  return (
    // Outer wrapper — tall enough to provide scroll travel for the reveal
    <div ref={wrapperRef} style={{ height: `${TOTAL_H}px` }}>
      {/* Sticky container — locks in view during the scroll-reveal phase */}
      <section
        className="relative text-white overflow-hidden"
        style={{ height: `${VIEWPORT_H}px`, position: 'sticky', top: 0 }}
      >
        {/* Slide images — taller than the section, shifted up as user scrolls */}
        {slides.map((s, i) => (
          <img
            key={s.image}
            src={s.image}
            alt=""
            className="absolute inset-x-0 top-0 w-full object-cover transition-opacity duration-1000"
            style={{
              opacity: i === slide ? 1 : 0,
              // Image is TOTAL_H tall so bottom isn't cut prematurely
              height: `${TOTAL_H}px`,
              // Shift upward to reveal hidden bottom; smooth via CSS will-change
              transform: `translateY(-${imgOffset}px)`,
              willChange: 'transform',
            }}
          />
        ))}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

        {/* Scroll-reveal progress bar — thin gold line at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10">
          <div
            className="h-full bg-gold transition-none"
            style={{ width: `${(imgOffset / IMAGE_EXTRA) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="relative h-full max-w-7xl mx-auto px-6 flex flex-col justify-center">
          <div className="max-w-2xl">
            <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm font-display mb-5">
              {current.badge}
            </div>
            <h1 className="font-heading text-4xl md:text-6xl leading-tight drop-shadow-lg whitespace-pre-line">
              {current.heading}
            </h1>
            <p className="mt-4 text-lg text-white/80 max-w-lg drop-shadow">
              {current.sub}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={current.ctaLink} className="btn-gold px-8 py-3 text-base">
                {current.cta}
              </Link>
              <a href="tel:0123231520" className="flex items-center gap-2 px-6 py-3 border-2 border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors font-display font-semibold backdrop-blur-sm">
                <Phone size={18} /> Call Us
              </a>
            </div>
          </div>

          {/* Slide dots */}
          <div className="flex gap-2 mt-10">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlide(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === slide ? 'w-8 bg-gold' : 'w-4 bg-white/30 hover:bg-white/50'}`}
              />
            ))}
          </div>

          {/* Scroll hint — fades out once reveal starts */}
          <div
            className="absolute bottom-6 right-6 flex flex-col items-center gap-1 transition-opacity duration-300"
            style={{ opacity: imgOffset > 10 ? 0 : 0.6 }}
          >
            <span className="text-white text-xs font-display tracking-widest uppercase">Scroll</span>
            <div className="w-px h-6 bg-white/50 animate-pulse" />
          </div>
        </div>
      </section>
    </div>
  )
}

function DemoProducts() {
  const demos = [
    { id: 1, product_name: 'Jameson Irish Whiskey 750ml', brand: 'Jameson', inclprice2: 359.99 },
    { id: 2, product_name: 'Jack Daniel\'s Old No.7 750ml', brand: 'Jack Daniel\'s', inclprice2: 299.99 },
    { id: 3, product_name: 'Heineken 330ml 24 Pack', brand: 'Heineken', inclprice2: 449.99 },
    { id: 4, product_name: 'Four Cousins Red Wine 750ml', brand: 'Four Cousins', inclprice2: 89.99 },
    { id: 5, product_name: 'Johnnie Walker Red Label 750ml', brand: 'Johnnie Walker', inclprice2: 269.99 },
    { id: 6, product_name: 'Monster Energy 440ml', brand: 'Monster', inclprice2: 39.99 },
    { id: 7, product_name: 'Smirnoff Vodka 750ml', brand: 'Smirnoff', inclprice2: 199.99 },
    { id: 8, product_name: 'Castle Lager 500ml 24 Pack', brand: 'Castle', inclprice2: 399.99 },
  ]
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
      {demos.map(p => <ProductCard key={p.id} product={p} />)}
    </div>
  )
}

function PromoBanner() {
  return (
    <section className="py-12 px-4 bg-gray-50 dark:bg-gray-800/50">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary-dark p-8 text-white">
          <div className="absolute right-0 top-0 text-[120px] opacity-10 leading-none">🍷</div>
          <h3 className="font-heading text-2xl">Wine Collection</h3>
          <p className="text-red-200 mt-2 mb-5">Handpicked local and imported wines for every occasion.</p>
          <Link to="/shop?category=wine" className="inline-flex items-center gap-2 bg-white text-primary font-display font-bold px-5 py-2.5 rounded-lg hover:bg-gray-100 transition-colors">
            Shop Wine <ArrowRight size={16} />
          </Link>
        </div>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-900 to-amber-800 p-8 text-white">
          <div className="absolute right-0 top-0 text-[120px] opacity-10 leading-none">🥃</div>
          <h3 className="font-heading text-2xl">Premium Spirits</h3>
          <p className="text-amber-200 mt-2 mb-5">Top-shelf whiskeys, brandies, and spirits at distributor prices.</p>
          <Link to="/shop?category=spirits" className="inline-flex items-center gap-2 bg-gold text-gray-900 font-display font-bold px-5 py-2.5 rounded-lg hover:bg-gold-dark transition-colors">
            Shop Spirits <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  )
}

function StatsSection() {
  const stats = [
    { value: '5,000+', label: 'Products' },
    { value: '30+', label: 'Years Experience' },
    { value: '10,000+', label: 'Happy Customers' },
    { value: '500+', label: 'Brands' },
  ]
  return (
    <section className="py-12 px-4 bg-primary text-white">
      <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {stats.map(s => (
          <div key={s.label}>
            <div className="font-heading text-4xl text-gold">{s.value}</div>
            <div className="text-red-200 mt-1 font-display">{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  )
}
